import type { ConfigType } from 'dayjs'
import type {
  DeliveryRouteStop,
  DeliveryShare,
} from '@/lib/services/delivery-shares/delivery-shares.types'
import type { DeliveryRouteDisplayStop } from './delivery-route-stops'
import type { NumericInput } from '@/lib/utils'

import { formatPhone } from '@/hooks/use-phone-formatter'
import dayjs from '@/lib/dayjs'
import { ADMIN_FONT_FAMILY, getAdminFontFaceCss } from '@/lib/admin-font'
import { getLineItemDisplay } from '@/lib/line-item-display'
import {
  formatDeliveryRouteDate,
  getDeliveryRouteDateSummary,
  getDeliveryRouteLocationAddress,
  getDeliveryRouteLocationLabel,
} from './delivery-share.utils'

// Manual display stops omit their order id, reference and items; all other fields retain the serializer shape.
type PrintRouteStop = Pick<
  DeliveryRouteDisplayStop,
  | 'source_type'
  | 'recipient_name'
  | 'recipient_phone'
  | 'fulfillment_due_date'
  | 'delivery_address'
  | 'special_instructions'
  | 'is_delivered'
  | 'is_route_missed'
  | 'sales_order_id'
  | 'delivery_destination_name'
  | 'purchaser_name'
  | 'purchaser_phone'
  | 'card_message'
  | 'signature'
> & {
  id?: DeliveryRouteStop['id'] | null
  reference_id?: DeliveryRouteStop['reference_id']
  sales_order_items?: DeliveryRouteStop['sales_order_items']
}
type PrintShare = Pick<
  DeliveryShare,
  'name' | 'share_url' | 'location' | 'share_recipients' | 'delivery_date'
>
interface DeliveryRoutePrintOptions {
  printedAt?: ConfigType
  share?: PrintShare | null
  shareQrCodeDataUrl?: string | null
  stops?: readonly PrintRouteStop[] | null
}

function escapeHtml(value: NumericInput): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}

function renderOptionalValue(value: NumericInput, fallback = '—'): string {
  const normalizedValue = String(value ?? '').trim()

  return escapeHtml(normalizedValue || fallback)
}

function formatAddress(
  address: DeliveryRouteStop['delivery_address'] | undefined,
  deliveryDestinationName: string | null | undefined
): string {
  if (!address) {
    return 'No address on file'
  }

  const cityAndState = [address.city, address.state].filter(Boolean).join(', ')
  const cityLine = [cityAndState, address.postal_code].filter(Boolean).join(' ')

  return [
    deliveryDestinationName,
    address.line1,
    address.line2,
    cityLine,
    address.country && address.country !== 'US' ? address.country : null,
  ]
    .filter(Boolean)
    .map(escapeHtml)
    .join('<br>')
}

function getDriverLabel(share: PrintShare | null | undefined): string {
  const labels = (share?.share_recipients ?? [])
    .map((recipient) => recipient?.name || recipient?.contact?.name || recipient?.destination)
    .map((label) => String(label ?? '').trim())
    .filter(Boolean)

  return [...new Set(labels)].join(', ') || 'Not assigned'
}

function getOrderLabel(stop: PrintRouteStop): string {
  if (stop.source_type === 'manual') {
    return 'Custom stop'
  }

  return `#${stop.reference_id || stop.sales_order_id || stop.id}`
}

function renderItems(stop: PrintRouteStop): string {
  const items = Array.isArray(stop.sales_order_items) ? stop.sales_order_items : []

  if (items.length === 0) {
    return '<p class="empty-value">No items listed</p>'
  }

  return `
    <table class="items-table">
      <thead>
        <tr>
          <th class="quantity-column">Qty</th>
          <th>Item</th>
        </tr>
      </thead>
      <tbody>
        ${items
          .map((item) => {
            const display = getLineItemDisplay(item)
            const quantity = Number(item?.quantity)
            const itemQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1
            const variantLabel = display.badgeLabel
              ? ` <span class="item-variant">(${escapeHtml(display.badgeLabel)})</span>`
              : ''

            return `
              <tr>
                <td class="quantity-column">${itemQuantity}</td>
                <td>${escapeHtml(display.primaryName)}${variantLabel}</td>
              </tr>
            `
          })
          .join('')}
      </tbody>
    </table>
  `
}

function renderStop(stop: PrintRouteStop, index: number): string {
  const recipientPhone = formatPhone(stop.recipient_phone)
  const purchaserPhone = formatPhone(stop.purchaser_phone)
  const deliveredMark = stop.is_delivered ? ' checked' : ''
  const missedMark = stop.is_route_missed ? ' checked' : ''
  const cardDetails =
    stop.source_type === 'manual'
      ? ''
      : `
      <div class="card-details">
        <div><span class="field-label">Card message:</span> ${renderOptionalValue(stop.card_message)}</div>
        <div><span class="field-label">Card signature:</span> ${renderOptionalValue(stop.signature)}</div>
      </div>
    `

  return `
    <section class="route-stop" data-stop-number="${index + 1}">
      <div class="stop-heading">
        <div class="stop-number">${index + 1}</div>
        <div>
          <div class="eyebrow">Stop</div>
          <div class="stop-title">${renderOptionalValue(stop.recipient_name, 'Unnamed recipient')}</div>
        </div>
        <div class="order-summary">
          <div><span class="field-label">Order:</span> ${escapeHtml(getOrderLabel(stop))}</div>
          <div><span class="field-label">Delivery date:</span> ${escapeHtml(formatDeliveryRouteDate(stop.fulfillment_due_date))}</div>
        </div>
      </div>

      <div class="stop-grid">
        <div class="detail-cell">
          <div class="field-label">Recipient</div>
          <div class="detail-primary">${renderOptionalValue(stop.recipient_name, 'Unnamed recipient')}</div>
          <div>${renderOptionalValue(recipientPhone)}</div>
          <div class="address">${formatAddress(stop.delivery_address, stop.delivery_destination_name)}</div>
        </div>
        <div class="detail-cell">
          <div class="field-label">Sender</div>
          <div class="detail-primary">${renderOptionalValue(stop.purchaser_name)}</div>
          <div>${renderOptionalValue(purchaserPhone)}</div>
        </div>
        <div class="detail-cell instructions-cell">
          <div class="field-label">Delivery instructions</div>
          <div>${renderOptionalValue(stop.special_instructions, 'No special instructions')}</div>
        </div>
      </div>

      <div class="stop-lower-grid">
        <div>
          <div class="field-label section-label">Items</div>
          ${renderItems(stop)}
          ${cardDetails}
        </div>
        <div class="completion-box">
          <div class="completion-options">
            <span class="checkbox${deliveredMark}"></span> Delivered
            <span class="checkbox${missedMark}"></span> Could not deliver
          </div>
          <div class="write-line"><span>Signature</span></div>
          <div class="write-line"><span>Delivery time</span></div>
          <div class="write-line"><span>Notes</span></div>
        </div>
      </div>
    </section>
  `
}

export function buildDeliveryRoutePrintHtml({
  printedAt,
  share,
  shareQrCodeDataUrl,
  stops,
}: DeliveryRoutePrintOptions): string {
  const routeStops: readonly PrintRouteStop[] = Array.isArray(stops) ? stops : []
  const routeName = String(share?.name ?? '').trim() || 'Delivery Route'
  const printedDate = dayjs(printedAt).format('MMM D, YYYY h:mm A')
  const deliveryDate = getDeliveryRouteDateSummary({ ...share, stops: routeStops })
  const shareUrl = String(share?.share_url ?? '').trim()
  const shareQrCode =
    shareQrCodeDataUrl && shareUrl
      ? `
        <a class="route-qr-link" href="${escapeHtml(shareUrl)}">
          <img
            class="route-qr"
            src="${escapeHtml(shareQrCodeDataUrl)}"
            alt="Scan to open delivery route"
          >
        </a>
        <div class="route-qr-caption">Scan to open route</div>
      `
      : ''

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${escapeHtml(routeName)} - Delivery List</title>
    <style>
      ${getAdminFontFaceCss()}
      @page { size: letter portrait; margin: 0.4in; }
      * { box-sizing: border-box; }
      html { color: #111827; font-family: ${ADMIN_FONT_FAMILY}; font-size: 9pt; }
      body { margin: 0; }
      .manifest-header { border-bottom: 2px solid #111827; padding-bottom: 10px; position: relative; }
      .manifest-title-row { padding-right: 1.15in; }
      h1 { font-size: 18pt; line-height: 1.1; margin: 0 0 4px; }
      .header-meta { display: grid; grid-template-columns: auto 1fr; gap: 3px 8px; margin: 10px 0 0; }
      .header-meta dt { font-weight: 700; }
      .header-meta dd { margin: 0; }
      .manifest-header-side { align-items: end; display: flex; flex-direction: column; gap: 4px; position: absolute; right: 0; top: 0; }
      .route-qr-link { display: block; line-height: 0; }
      .route-qr { display: block; height: 0.9in; image-rendering: pixelated; width: 0.9in; }
      .route-qr-caption { font-size: 7pt; font-weight: 700; line-height: 1.1; text-align: right; }
      .printed-at { color: #4b5563; font-size: 8pt; text-align: right; white-space: nowrap; }
      .route-stop { border-top: 1.5px solid #111827; break-inside: avoid-page; page-break-inside: avoid; padding: 10px 0 2px; }
      .route-stop:first-of-type { border-top: 0; }
      .stop-heading { display: grid; grid-template-columns: 32px minmax(0, 1fr) auto; align-items: center; gap: 8px; }
      .stop-number { align-items: center; border: 1.5px solid #111827; border-radius: 50%; display: flex; font-size: 12pt; font-weight: 700; height: 28px; justify-content: center; width: 28px; }
      .eyebrow, .field-label { color: #374151; font-size: 7.5pt; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
      .stop-title { font-size: 12pt; font-weight: 700; }
      .order-summary { display: grid; gap: 2px; min-width: 175px; text-align: right; }
      .stop-grid { border-bottom: 1px dotted #9ca3af; border-top: 1px dotted #9ca3af; display: grid; grid-template-columns: 1.15fr 0.85fr 1fr; margin-top: 8px; }
      .detail-cell { min-width: 0; padding: 7px 10px 7px 0; }
      .detail-cell + .detail-cell { border-left: 1px dotted #9ca3af; padding-left: 10px; }
      .detail-primary { font-weight: 700; margin: 2px 0; }
      .address { line-height: 1.3; margin-top: 4px; }
      .stop-lower-grid { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(230px, 0.65fr); gap: 16px; padding: 8px 0; }
      .section-label { margin-bottom: 4px; }
      .items-table { border-collapse: collapse; width: 100%; }
      .items-table th { border-bottom: 1px solid #9ca3af; color: #374151; font-size: 7.5pt; padding: 2px 4px; text-align: left; text-transform: uppercase; }
      .items-table td { border-bottom: 1px dotted #d1d5db; padding: 3px 4px; vertical-align: top; }
      .quantity-column { text-align: center !important; width: 38px; }
      .item-variant, .empty-value { color: #4b5563; }
      .empty-value { margin: 3px 0; }
      .card-details { display: grid; gap: 3px; margin-top: 6px; }
      .completion-box { border: 1.5px solid #111827; min-height: 92px; padding: 7px 8px; }
      .completion-options { display: flex; gap: 12px; margin-bottom: 7px; }
      .checkbox { border: 1px solid #111827; display: inline-block; height: 10px; margin-right: 3px; position: relative; top: 1px; width: 10px; }
      .checkbox.checked::after { content: '✓'; font-size: 11px; font-weight: 700; left: 0; line-height: 8px; position: absolute; top: -1px; }
      .write-line { border-bottom: 1px solid #6b7280; height: 20px; position: relative; }
      .write-line span { background: white; color: #4b5563; font-size: 7pt; left: 0; padding-right: 5px; position: absolute; top: 8px; text-transform: uppercase; }
      @media screen { body { margin: 0.4in; } }
    </style>
  </head>
  <body>
    <header class="manifest-header">
      <div class="manifest-title-row">
        <div>
          <h1>${escapeHtml(routeName)}</h1>
          <div>${escapeHtml(getDeliveryRouteLocationLabel(share?.location))}</div>
          <div>${escapeHtml(getDeliveryRouteLocationAddress(share?.location))}</div>
        </div>
        <div class="manifest-header-side">
          ${shareQrCode}
          <div class="printed-at">Printed ${escapeHtml(printedDate)}</div>
        </div>
      </div>
      <dl class="header-meta">
        <dt>Delivery date</dt><dd>${escapeHtml(deliveryDate)}</dd>
        <dt>Driver</dt><dd>${escapeHtml(getDriverLabel(share))}</dd>
        <dt>Stops</dt><dd>${routeStops.length}</dd>
      </dl>
    </header>
    <main>
      ${routeStops.map(renderStop).join('')}
    </main>
  </body>
</html>`
}
